import { Component, ElementRef, inject, input, QueryList, signal, ViewChildren, WritableSignal } from '@angular/core';
import { CheckpointFormWrapper } from '@pages/checkpoint-detail-page/checkpoint-form';
import { createTextBlockForm } from '@pages/checkpoint-detail-page/checkpoint-utils';
import { NbToastrService } from '@nebular/theme';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { take } from 'rxjs';

@Component({
  selector: 'wr-assessment-detail-form',
  standalone: false,
  templateUrl: './assessment-detail-form.component.html',
  styleUrl: './assessment-detail-form.component.scss'
})
export class AssessmentDetailFormComponent {
  formWrapper = input.required<CheckpointFormWrapper>();
  isEditing = input.required<boolean>();
  currentTenantId = input.required<string | undefined>();
  toDeleteAssessmentPreset = input.required<WritableSignal<string[]>>();
  toDeleteTextBlocks = input.required<WritableSignal<string[]>>();

  readonly assessmentPresetStatusFilter = signal<boolean | null>(null);

  @ViewChildren('assessmentItem') assessmentItems!: QueryList<ElementRef>;

  readonly toastrService = inject(NbToastrService);

  shouldShow(control: AbstractControl): boolean {
    const filter = this.assessmentPresetStatusFilter();
    if (filter === null) return true;
    return control.get('deprecated')?.value === filter;
  }

  addAssessment(): void {
    this.formWrapper().addAssessmentPreset();
    this.toastrService.success('Vordefinierte Bewertung wurde hinzugefügt.', 'Prüfpunkt');
    this.assessmentItems.changes.pipe(take(1)).subscribe(() => {
      const firstItem = this.assessmentItems.first?.nativeElement as HTMLElement | undefined;
      (firstItem?.querySelector('wr-action-select button') as HTMLElement | null)?.focus();
    });
  }

  get assessmentPresets(): FormArray {
    return this.formWrapper().form().controls.assessmentPresets;
  }

  showStandardEquipmentSelect(control: AbstractControl): boolean {
    const group = control as FormGroup;
    const value = group.get('buildingEquipmentRelated')?.value;
    if (!value) {
      group.get('standardEquipment')?.setValue(null);
    }
    return !!value;
  }

  private readonly expandedTextBlocks = new Set<AbstractControl>();

  textBlocks(control: AbstractControl): FormArray {
    return control.get('textBlocks') as FormArray;
  }

  isTextBlocksExpanded(control: AbstractControl): boolean {
    return this.expandedTextBlocks.has(control);
  }

  toggleTextBlocks(control: AbstractControl): void {
    if (!this.expandedTextBlocks.delete(control)) {
      this.expandedTextBlocks.add(control);
    }
  }

  expandInvalidTextBlockSections(): void {
    this.assessmentPresets.controls.forEach((control) => {
      if (this.textBlocks(control)?.invalid) {
        this.expandedTextBlocks.add(control);
      }
    });
  }

  addTextBlock(control: AbstractControl): void {
    this.textBlocks(control).push(createTextBlockForm());
    this.textBlocks(control).markAsDirty();
    this.expandedTextBlocks.add(control);
  }

  removeTextBlock(control: AbstractControl, index: number): void {
    const textBlocks = this.textBlocks(control);
    const id = textBlocks.at(index)?.get('id')?.value;
    if (id) {
      this.toDeleteTextBlocks().update((ids) => [...ids, id]);
    }
    textBlocks.removeAt(index);
    textBlocks.markAsDirty();
  }

  moveTextBlock(control: AbstractControl, index: number, delta: number): void {
    const textBlocks = this.textBlocks(control);
    const target = index + delta;
    if (target < 0 || target >= textBlocks.length) return;
    const textBlock = textBlocks.at(index);
    textBlocks.removeAt(index);
    textBlocks.insert(target, textBlock);
    textBlocks.markAsDirty();
  }
}
